import * as React from 'react'
import { Slot as SlotPrimitive } from 'radix-ui'
import { PanelRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

type SidebarContextValue = {
  open: boolean
  setOpen: (value: boolean | ((v: boolean) => boolean)) => void
  openMobile: boolean
  setOpenMobile: (value: boolean | ((v: boolean) => boolean)) => void
  isMobile: boolean
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null)

export function useSidebar(): SidebarContextValue {
  const ctx = React.useContext(SidebarContext)
  if (!ctx) {
    throw new Error('useSidebar must be used within a SidebarProvider.')
  }
  return ctx
}

type SidebarProviderProps = {
  children: React.ReactNode
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function SidebarProvider({
  children,
  defaultOpen = true,
  open: openProp,
  onOpenChange,
}: SidebarProviderProps) {
  const [internalOpen, _setOpen] = React.useState<boolean>(defaultOpen)
  const open = openProp ?? internalOpen
  const [openMobile, setOpenMobile] = React.useState<boolean>(false)
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth < 768
  })

  // Track viewport to know when we are on mobile
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // effective open state that reflect both destktop and mobile open states
  const effectiveOpen = isMobile ? openMobile : open

  const setOpen = React.useCallback(
    (value: boolean | ((v: boolean) => boolean)) => {
      if (onOpenChange) {
        const next =
          typeof value === 'function'
            ? (value as (v: boolean) => boolean)(open)
            : value
        onOpenChange(next)
      } else {
        _setOpen((current) => {
          const next =
            typeof value === 'function'
              ? (value as (v: boolean) => boolean)(current)
              : value
          // In uncontrolled mode, persist cookie based on the computed next state
          try {
            document.cookie = `hoku_sidebar_open=${String(next)}; path=/; max-age=${60 * 60 * 24 * 365}`
          } catch {}
          return next
        })
      }
    },
    [onOpenChange, open]
  )

  return (
    <SidebarContext.Provider
      value={{
        open: effectiveOpen,
        setOpen,
        openMobile,
        setOpenMobile,
        isMobile,
      }}>
      <div
        data-sidebar-provider
        data-state={effectiveOpen ? 'open' : 'closed'}
        className="contents">
        {children}
      </div>
    </SidebarContext.Provider>
  )
}

type SidebarProps = React.ComponentProps<'aside'> & {
  className?: string
  children: React.ReactNode
}

export function Sidebar({ className, children, ...props }: SidebarProps) {
  const { open } = useSidebar()

  return (
    <aside
      data-slot="sidebar"
      data-collapsible="icon"
      data-state={open ? 'open' : 'closed'}
      className={cn(
        'group/sidebar relative flex h-full shrink-0 flex-col bg-sidebar text-sidebar-foreground',
        className
      )}
      {...props}>
      {children}
    </aside>
  )
}

export function SidebarHeader({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-header"
      className={cn('sticky top-0 flex items-center', className)}
      {...props}
    />
  )
}

export function SidebarContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-content"
      className={cn('min-h-0 flex-1 overflow-y-auto', className)}
      {...props}
    />
  )
}

export function SidebarFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-footer"
      className={cn('sticky bottom-0', className)}
      {...props}
    />
  )
}

export function SidebarInset({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-inset"
      className={cn(
        'group/sidebar-wrapper flex min-h-0 flex-1 flex-col',
        className
      )}
      {...props}
    />
  )
}

export function SidebarGroup({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group"
      className={cn('mb-4', className)}
      {...props}
    />
  )
}

export function SidebarGroupLabel({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group-label"
      className={cn(
        'mx-2 mb-2 select-none text-[11px] font-medium text-sidebar-foreground/60 group-data-[state=closed]/sidebar:hidden',
        className
      )}
      {...props}>
      <div className="flex items-center gap-2">
        <div className="h-px w-4 bg-border/60" />
        <span>{children}</span>
        <div className="h-px flex-1 bg-border/60" />
      </div>
    </div>
  )
}

export function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group-content"
      className={cn('px-1', className)}
      {...props}
    />
  )
}

export function SidebarMenu({
  className,
  ...props
}: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu"
      className={cn('grid grid-cols-[minmax(0,1fr)] gap-1', className)}
      {...props}
    />
  )
}

export function SidebarMenuItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-item"
      className={cn('list-none sidebar-menu-item cursor-pointer', className)}
      {...props}
    />
  )
}

type SidebarMenuButtonProps = React.ComponentProps<'a'> & {
  asChild?: boolean
  isActive?: boolean
}

export function SidebarMenuButton({
  className,
  asChild,
  isActive,
  children,
  ...props
}: SidebarMenuButtonProps) {
  const Comp = asChild ? SlotPrimitive.Slot : ('a' as any)
  return (
    <Comp
      data-slot="sidebar-menu-button"
      data-active={isActive ? 'true' : 'false'}
      className={cn(
        'group/menu-button flex w-full cursor-pointer items-center gap-3 rounded-md p-2 text-sm text-sidebar-foreground/85 hover:bg-neutral-hover-bg hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/30',
        isActive && 'bg-neutral-hover-bg text-sidebar-foreground',
        // hide label in icon mode
        '[&>span]:data-[collapsible=icon]/sidebar:hidden',
        className
      )}
      {...props}>
      {children}
    </Comp>
  )
}

type SidebarTriggerProps = React.ComponentProps<'button'> & {
  children?: React.ReactNode
  className?: string
}

export function SidebarTrigger({
  className,
  children,
  ...props
}: SidebarTriggerProps) {
  const { setOpen, setOpenMobile, isMobile } = useSidebar()

  return (
    <Button
      variant="ghost"
      size="icon"
      data-slot="sidebar-trigger"
      onClick={() => (isMobile ? setOpenMobile((v) => !v) : setOpen((v) => !v))}
      className={cn('bg-background/50 hover:bg-neutral-hover-bg', className)}
      {...props}>
      {children ?? <PanelRight size={16} aria-hidden />}
    </Button>
  )
}

/**
 * The 40x40 affordance every control on the icon rail wears — category
 * buttons, search, notifications, support.
 *
 * Lives here rather than in AppSidebar so the notification bell can dress
 * itself like the rail without a feature module importing the app shell, and
 * so a change to the rail's hover or focus treatment lands in one place.
 *
 * `shrink-0` is load-bearing, not decoration: in a rail too short for its
 * contents the flex column would otherwise squeeze these below their 40px hit
 * area before the container scrolls.
 */
export const RAIL_BUTTON_CLASS =
  'size-10 shrink-0 rounded-lg flex items-center justify-center text-primary-high-contrast hover:bg-primary-high-contrast/[0.07] focus-visible:outline-2 focus-visible:outline-primary-solid focus-visible:-outline-offset-2 transition-colors duration-fast'

/**
 * The selected treatment for a rail control whose page you are on — the
 * design system's `Sidebar item horizontal` in its active state. A different
 * colour from the hover wash above on purpose: "where I am" must not read as
 * "what I'm pointing at".
 */
export const RAIL_BUTTON_ACTIVE_CLASS =
  'bg-primary-ui-bg [&_svg]:text-primary-solid'
